import { useAuth } from '@agentnet/auth';
import { Container, createStyles, makeStyles, Theme } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { deleteCPL, getCPLDetail, unvoidCPL, voidCPL } from 'api/cpl-api';
import { GetFileInfoProps } from 'api/file/file-api';
import { Profile } from 'api/profile/types';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import useAsync from 'hooks/useAsync';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useParams } from 'react-router-dom';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import AccordionContent from 'ui-kit/components/accordion/AccordionContent';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import { AgentNetDivider as NewAgentNetDivider } from 'ui-kit/components/dividers/AgentNetDivider2';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import { Notification } from 'ui-kit/components/notification/Notification';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import NoResultFoundIcon from 'ui-kit/icons/NoResultFound';
import { openDocument } from 'utilities/utilities';
import { DocumentType, KBLinks, newWindow, WindowFeatures } from '../../constants';
import CplDocumentUploaderFC from '../CplDocumentUploader';
import CPLEdit from '../cpl-edit/CPLEdit';
import { cplData, cplList } from '../types';
import {
  CPLActions,
  cplActiveActionProps,
  CPLErrorCodes,
  CPLStatus,
  MANUAL_CPL_ACTIVITY_ID,
  noCplsFound,
  openCplsLimit,
} from './constants';

interface CplListProps {
  onCancel: () => void;
  cplListData: cplList | null;
  isCplClicked?: boolean;
  handleAddNewCPL?: () => void;
  isStarsStatusActive?: boolean;
  noStarsStatusActive?: boolean;
  updateOpenPendingCount: (cpls: cplData[]) => void;
  profile?: Profile | null | undefined;
  isAccountActive?: boolean;
}

const useStyles = makeStyles((theme: Theme) => createStyles({ warningMsg: { marginBottom: theme.spacing(3) } }));
const CplList: React.FC<CplListProps> = ({
  onCancel,
  cplListData,
  handleAddNewCPL,
  isStarsStatusActive,
  noStarsStatusActive,
  updateOpenPendingCount,
  profile,
  isAccountActive,
}: CplListProps) => {
  const classes = useStyles();
  const [expandedCplIds, setExpandedCplIds] = useState<any[]>([]);
  const isUserAllowedManualCpl = profile?.activityRights?.some((x) => x.ActivityRightId === MANUAL_CPL_ACTIVITY_ID);

  const reducer = (state: any, action: any) => {
    switch (action.type) {
      case CPLActions.edit:
        return {
          ...state,
          isEditing: true,
          selectedCplId: action.payload.id,
          coveredPartyId: action.payload.coveredPartyId,
          validationCode: action.payload.validationCode,
        };
      case CPLActions.view:
        return {
          ...state,
          isEditing: false,
          selectedCplId: '',
          coveredPartyId: null,
          validationCode: null,
          closeAllActive: !state.closeAllActive,
        };
      case CPLActions.expandCollapseActive:
        return {
          ...state,
          isExpandActive: action.payload,
          closeAllActive: !action.payload,
        };
      case CPLActions.expandCollapseInActive:
        return {
          ...state,
          isExpandInActive: action.payload,
          closeAllInActive: !action.payload,
        };
      case CPLActions.onInactiveToggleChange:
        return { ...state, isInactiveToggle: !state.isInactiveToggle };
      case 'OnExpandChangeActive':
        return { ...state, closeAllActive: false };
      case 'OnExpandChangeInActive':
        return { ...state, closeAllInActive: false };
      case 'OnChangingCplId':
        return {
          ...state,
          selectedCplId: action.payload.id,
          coveredPartyId: action.payload.coveredPartyId,
          validationCode: action.payload.validationCode ?? null,
        };
      case 'OnSuccessVoid':
        return { ...state, coveredPartyId: null, validationCode: null };
      default:
        throw new Error();
    }
  };

  const [cplActionsState, dispatch] = useReducer(reducer, {
    isEditing: false,
    selectedCplId: '',
    isExpandActive: false,
    isExpandInActive: false,
    isInactiveToggle: false,
    closeAllActive: false,
    closeAllInActive: false,
    validationCode: null,
  });
  const [editedPartyName, setEditedPartyName] = useState<string>('');
  const [activeAction, setactiveAction] = useState<cplActiveActionProps | null>(null);
  const [actionListModal, setActionListModal] = useState<boolean>(false);
  const [activeActionPos, setactiveActionPos] = useState<any>();
  const [topPos, setTopPos] = useState<any[] | undefined>([]);
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string>('');
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openVoidModal, setOpenVoidModal] = useState<boolean>(false);
  const [openUnvoidModal, setOpenUnvoidModal] = useState<boolean>(false);
  const [selectedCplId, setSelectedCplId] = useState<string | number>('');
  const [uploadCplDocument, setuploadCplDocument] = useState<boolean>(false);
  const [CPLs, updateCPLList] = useState<cplData[]>(cplListData?.cpls ?? []);
  const [errMsgs, setErrMsgs] = useState([]);
  const { fileId } = useParams<GetFileInfoProps>();
  const { getAccessToken } = useAuth();
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const { setActiveTab, fileData, loadFileData } = fileDataCtx;
  const activeCpls = CPLs.filter(
    (item) =>
      item.status === CPLStatus.open ||
      item.status === CPLStatus.pending ||
      item.status === CPLStatus.closed ||
      item.status === CPLStatus.pending_remit ||
      item.status === CPLStatus.remitted,
  );
  const inactiveCpls = CPLs.filter((item) => item.status === CPLStatus.void);
  const activeCplsCount = activeCpls.length ?? 0;

  const { addGlobalMsg } = useGlobalMessages();
  const { addSnackbarMessage } = useSnackBars();

  const deleteSingleCPL = async (): Promise<unknown> => {
    const token = await getAccessToken();
    return deleteCPL(fileId ?? '', cplActionsState.selectedCplId ?? '', cplActionsState.coveredPartyId, token);
  };

  const voidSingleCPL = async (): Promise<unknown> => {
    const token = await getAccessToken();
    return voidCPL(
      fileId ?? '',
      cplActionsState.selectedCplId ?? '',
      token,
      fileData?.accountId,
      cplActionsState?.coveredPartyId,
    );
  };

  const unvoidSingleCPL = async (): Promise<unknown> => {
    const token = await getAccessToken();
    return unvoidCPL(
      fileId ?? '',
      cplActionsState.selectedCplId ?? '',
      token,
      fileData?.accountId,
      cplActionsState?.coveredPartyId,
    );
  };

  const getCplPdf = async (): Promise<unknown> => {
    const token = await getAccessToken();
    return getCPLDetail(fileId ?? '', fileData?.accountId, token, selectedCplId ?? '', cplActionsState.validationCode);
  };

  const {
    execute: executeCplPdf,
    value: pdfValue,
    status: pdfStatus,
    errors: getPdfError,
  } = useAsync<any>(getCplPdf, false);

  const {
    execute: executeDeleteCpl,
    value: deleteCplValue,
    status: deleteStatus,
    errors: deleteCplError,
  } = useAsync<any>(deleteSingleCPL, false);

  const {
    execute: executeVoidCpl,
    value: voidCplValue,
    status: voidStatus,
    errors: voidCplError,
  } = useAsync<any>(voidSingleCPL, false);

  const {
    execute: executeUnvoidCpl,
    value: unvoidCplValue,
    status: unvoidStatus,
    errors: unvoidCplError,
  } = useAsync<any>(unvoidSingleCPL, false);

  useEffect(() => {
    if ((voidStatus === 'success' || unvoidStatus === 'success' || deleteStatus === 'success') && fileId) {
      loadFileData && loadFileData({ fileId });
    }
    setDisplayError(
      pdfStatus === 'error' || deleteStatus === 'error' || voidStatus === 'error' || unvoidStatus === 'error',
    );
  }, [pdfStatus, deleteStatus, voidStatus, unvoidStatus]);

  useEffect(() => {
    updateOpenPendingCount(CPLs);
  }, [CPLs]);

  useEffect(() => {
    const errorMessages = getPdfError?.length
      ? getPdfError
      : deleteCplError?.length
      ? deleteCplError
      : voidCplError?.length
      ? voidCplError
      : unvoidCplError?.length
      ? unvoidCplError
      : [];
    setErrMsgs(errorMessages);
  }, [displayError]);

  useEffect(() => {
    if (displayError && errMsgs.length) {
      errMsgs?.map((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [displayError, errMsgs]);

  useEffect(() => {
    if (deleteCplValue?.result?.cplId) {
      setDisplayError(false);
      addSnackbarMessage({
        message: 'Deleted CPL Successfully',
        type: 'success',
      });

      const cpls = CPLs?.filter(
        (cpl) =>
          !(
            cpl.cplId === deleteCplValue?.result?.cplId && cpl.coveredPartyId === deleteCplValue?.result?.coveredPartyId
          ),
      );
      if (!cpls || cpls.length === 0) {
        if (setActiveTab) setActiveTab('CPLCreate');
      } else {
        updateCPLList(cpls);
      }
    } else {
      if (deleteCplValue?.errorCode == CPLErrorCodes.notFound) {
        const cpls = CPLs?.filter((cpl) => cpl.cplId !== activeAction?.cpl.cplId);
        updateCPLList(cpls);
      }
    }
    setOpenDeleteModal(false);
  }, [deleteCplValue]);

  useEffect(() => {
    if (voidCplValue?.result?.cplId) {
      setDisplayError(false);
      addSnackbarMessage({
        message: 'Voided CPL Successfully',
        type: 'success',
      });
      const cpls = CPLs?.map((cpl: cplData) => {
        if (cpl.cplId === voidCplValue?.result?.cplId && cpl.coveredPartyId === voidCplValue?.result?.coveredPartyId) {
          cpl.status = voidCplValue?.result?.status ?? 'Void';
        }
        return cpl;
      });

      if (!cpls || cpls.length === 0) {
        if (setActiveTab) setActiveTab('CPLEdit');
      } else {
        updateCPLList(cpls);
      }
      // Clear the coveredPartyId and the CPL list has been updated
      dispatch({ type: 'OnSuccessVoid' });
    }
    setOpenVoidModal(false);
  }, [voidCplValue]);

  useEffect(() => {
    if (unvoidCplValue?.result?.cplId) {
      setDisplayError(false);
      addSnackbarMessage({
        message: 'Unvoided CPL Successfully',
        type: 'success',
      });
      const cpls = CPLs?.map((cpl: cplData) => {
        if (
          cpl.cplId === unvoidCplValue?.result?.cplId &&
          cpl.coveredPartyId === unvoidCplValue?.result?.coveredPartyId
        ) {
          cpl.status = unvoidCplValue?.result?.status ?? 'Open';
        }
        return cpl;
      });

      if (!cpls || cpls.length === 0) {
        if (setActiveTab) setActiveTab('CPLEdit');
      } else {
        updateCPLList(cpls);
      }
      // Clear the coveredPartyId and the CPL list has been updated
      dispatch({ type: 'OnSuccessVoid' });
    }
    setOpenUnvoidModal(false);
  }, [unvoidCplValue]);

  useEffect(() => {
    if (activeActionPos && activeActionPos.pageY) {
      setTopPos([activeActionPos.pageX, activeActionPos.pageY]);
    }
  }, [activeActionPos]);

  useEffect(() => {
    if (selectedCplId !== '') {
      executeCplPdf().then();
      setTimeout(() => setSelectedCplId(''), 3000);
    }
  }, [selectedCplId]);

  useEffect(() => {
    if (pdfStatus === 'success') {
      if (pdfValue?.pdfData) openDocument(pdfValue.pdfData, DocumentType, newWindow, WindowFeatures);
    }
  }, [pdfStatus]);

  useEffect(() => {
    if (displayError && errMsg) {
      addSnackbarMessage({
        message: errMsg,
        type: 'error',
        onClose: () => setDisplayError(false),
      });
    }
  }, [displayError, errMsg]);

  useEffect(() => {
    if (activeCplsCount === 0) {
      dispatch({ type: CPLActions.onInactiveToggleChange });
    }
  }, [activeCplsCount]);

  const toggleActionModal = (value: boolean) => {
    setActionListModal(value);
    if (!value) {
      setactiveAction(null);
    }
  };
  /* Handle Dialog Modal */
  const handleCloseDialog = () => {
    if (unvoidStatus === 'pending' || deleteStatus === 'pending' || voidStatus === 'pending') return;
    setOpenDeleteModal(false);
    setOpenVoidModal(false);
    setOpenUnvoidModal(false);
  };
  const handleDeleteCPLDialog = () => {
    executeDeleteCpl().then();
  };
  const handleVoidCPLDialog = () => {
    executeVoidCpl().then();
  };
  const handleUnvoidCPLDialog = () => {
    executeUnvoidCpl().then();
  };

  function getAccordianProps(cpl: cplData, index: number) {
    const i = index + 1;
    const currentSelectedCPL =
      cpl.cplId === cplActionsState.selectedCplId && cpl.coveredPartyId === cplActionsState.coveredPartyId;
    return {
      closeAll:
        (cpl.status !== CPLStatus.void && cplActionsState.closeAllActive) ||
        (cpl.status === CPLStatus.void && cplActionsState.closeAllInActive),
      expanded:
        (cpl.status !== CPLStatus.void && cplActionsState.isExpandActive) ||
        (cpl.status === CPLStatus.void && cplActionsState.isExpandInActive),
      isEditing: currentSelectedCPL && cplActionsState.isEditing,
      isEditDisabled:
        cplActionsState.isEditing ||
        noStarsStatusActive ||
        isStarsStatusActive ||
        cpl.isCplUploaded ||
        fileData?.fileStatus !== CPLStatus.open ||
        !isAccountActive,
      isUnvoidDisabled:
        cplActionsState.isEditing ||
        noStarsStatusActive ||
        isStarsStatusActive ||
        fileData?.fileStatus !== CPLStatus.open ||
        !isAccountActive,
      isVoidDisabled:
        (cplActionsState.isEditing && !currentSelectedCPL) ||
        noStarsStatusActive ||
        fileData?.fileStatus !== CPLStatus.open ||
        !isAccountActive,
      isCollapseDisabled: cplActionsState.isEditing || cpl.isCplUploaded,
      //isPdfDisabled: cplActionsState.isEditing,
      isDeleteDisabled: cplActionsState.isEditing || noStarsStatusActive || fileData?.fileStatus !== CPLStatus.open,
      accordianQAAttribute: `CPLSummary${cpl.status === CPLStatus.void ? 'Inactive' : 'Active'}${i}`,
      deleteQAAttribute: `CPL${i}Delete`,
      voidQAAttribute: `CPL${i}Void`,
      unvoidQAAttribute: `CPL${i}Unvoid`,
      editQAAttribute: `CPL${i}Edit`,
      pdfQAAttribute: `CPL${i}ViewPDF`,
      id: cpl.cplId,
      coveredPartyId: cpl.coveredPartyId,
      title: `${cpl.coveredParty} - ${cpl.letterType}`,
      type: 'cpl',
      status: cpl.status,
      subtitle: currentSelectedCPL && cplActionsState.isEditing ? [editedPartyName || cpl.partyName] : [cpl.partyName],
      createdDate: cpl.created,
      icon: 'document',
      className: 'cpl-accordion',
      onExpandChange: (expand: boolean, id: string | number) => {
        if (expand) {
          cpl.status === CPLStatus.void
            ? dispatch({ type: 'OnExpandChangeActive' })
            : dispatch({ type: 'OnExpandChangeInActive' });
          dispatch({
            type: 'OnChangingCplId',
            payload: { id: cpl.cplId, coveredPartyId: cpl.coveredPartyId, validationCode: cpl.validationCode },
          });
          setExpandedCplIds((prevIds) => [...prevIds, id]);
        } else {
          setExpandedCplIds((prevIds) => [...prevIds].filter((ele) => id !== ele));
        }
      },
      ...((cpl.status === CPLStatus.open || cpl.status === CPLStatus.pending) && {
        onEdit: (id: number | string, coveredPartyId?: number) => {
          dispatch({ type: CPLActions.edit, payload: { id, coveredPartyId } });
        },
      }),
      ...(cpl.status === CPLStatus.open && {
        onVoid: (id: number | string, coveredPartyId?: number) => {
          dispatch({ type: 'OnChangingCplId', payload: { id, coveredPartyId } });
          setOpenVoidModal(true);
        },
      }),
      ...((cpl.status === CPLStatus.open ||
        cpl.status === CPLStatus.closed ||
        cpl.status === CPLStatus.pending_remit ||
        cpl.status === CPLStatus.remitted) && {
        onPDF: (id: number | string) => {
          dispatch({
            type: 'OnChangingCplId',
            payload: { id, coveredPartyId: cpl.coveredPartyId, validationCode: cpl.validationCode ?? null },
          });
          setSelectedCplId(id);
        },
      }),
      ...(cpl.status === CPLStatus.pending && {
        onDelete: (id: number | string, coveredPartyId?: number) => {
          dispatch({ type: 'OnChangingCplId', payload: { id, coveredPartyId } });
          setOpenDeleteModal(true);
        },
      }),
      ...(cpl.status === CPLStatus.void && {
        onUnvoid: (id: number | string, coveredPartyId?: number) => {
          dispatch({ type: 'OnChangingCplId', payload: { id, coveredPartyId } });
          if (activeCplsCount >= 5) {
            setErrMsg(
              ' The maximum limit of 5 open and pending CPLs in this file has been reached. Please Void/Delete a CPL to continue.',
            );
            setDisplayError(true);
          } else {
            setErrMsg('');
            setOpenUnvoidModal(true);
          }
        },
      }),
    };
  }

  function getCplEditProps(currentCPL: string, coveredPartyId: number) {
    return {
      cplListData: cplListData,
      cplsCount: activeCplsCount,
      onCancel: onCancel,
      isStarsStatusActive: isStarsStatusActive,
      noStarsStatusActive: noStarsStatusActive,
      isEditMode:
        currentCPL === `${cplActionsState.selectedCplId}${cplActionsState.coveredPartyId ?? coveredPartyId}` &&
        cplActionsState.isEditing,
      coveredPartyId: cplActionsState.coveredPartyId ?? coveredPartyId,
    };
  }

  const handleShiftClickExpand = () => {
    // Check if all active CPLs are expanded
    const allActiveExpanded = activeCpls.every((cpl) => expandedCplIds.includes(`${cpl.cplId}${cpl.coveredPartyId}`));
    const allInactiveExpanded = inactiveCpls.every((cpl) =>
      expandedCplIds.includes(`${cpl.cplId}${cpl.coveredPartyId}`),
    );

    // For active CPLs
    dispatch({
      type: CPLActions.expandCollapseActive,
      payload: !allActiveExpanded,
    });

    // For inactive CPLs if they're visible
    if (cplActionsState.isInactiveToggle) {
      dispatch({
        type: CPLActions.expandCollapseInActive,
        payload: !allInactiveExpanded,
      });
    }
  };

  const renderCplDetails = (cpls: cplData[]) => {
    if (Array.isArray(cpls) && !cpls.length) return;
    return cpls.map((cpl, index) => (
      // deepcode ignore ReactXssDomContentSafe: Props are passed to React component which auto-escapes text content
      <AccordionContent
        key={`${cpl.cplId}${cpl.coveredPartyId}`}
        {...getAccordianProps(cpl, index)}
        onShiftClickExpand={handleShiftClickExpand}
      >
        {expandedCplIds.some((id) => `${cpl.cplId}${cpl.coveredPartyId}` === id) ? (
          <CPLEdit
            accordionView
            index={index + 1}
            cplId={cpl.cplId}
            {...getCplEditProps(`${cpl.cplId}${cpl.coveredPartyId}`, cpl.coveredPartyId)}
            onViewMode={() => {
              setEditedPartyName('');
              dispatch({ type: CPLActions.view });
            }}
            setEditedPartyName={setEditedPartyName}
            profile={profile}
          />
        ) : (
          <> </>
        )}
      </AccordionContent>
    ));
  };

  return (
    <>
      <LoadingSpinner status={pdfStatus} variant="linear" />
      {(isStarsStatusActive || noStarsStatusActive) && !CPLs?.length ? (
        <div className="no-results-msg">
          <NoResultFoundIcon className="no-cpls" />
          <Typography variant="h1">No CPLs Associated to This File</Typography>
          <Typography variant="body1">Due to inactive status, this file can not create CPLs.</Typography>
        </div>
      ) : (
        <>
          <div className="jackets-summary">
            <div className="paper-layout">
              <Container>
                <PageHeader
                  title="CPL"
                  subtitle="View and manage your Closing Protection Letters for this file. You can have up to 5 active CPLs at any time."
                  contentRight={
                    <div style={{ marginBottom: '12px' }}>
                      {isUserAllowedManualCpl && (
                        <AgentNetButton
                          color="primary"
                          variant="outlined"
                          disableFocusRipple
                          onClick={() => setuploadCplDocument(true)}
                          disabled={
                            activeCplsCount >= 5 ||
                            isStarsStatusActive ||
                            noStarsStatusActive ||
                            fileData?.fileStatus !== CPLStatus.open ||
                            (fileData?.fileStatus === CPLStatus.open && fileData?.serviceStatus?.isReopenedByProduct) ||
                            !isAccountActive
                          }
                          size="small"
                          style={{ marginRight: '10px' }}
                        >
                          Upload CPL
                        </AgentNetButton>
                      )}

                      <AgentNetButton
                        variant="contained"
                        color="primary"
                        onClick={handleAddNewCPL}
                        disabled={
                          activeCplsCount >= 5 ||
                          isStarsStatusActive ||
                          noStarsStatusActive ||
                          fileData?.fileStatus !== CPLStatus.open ||
                          (fileData?.fileStatus === CPLStatus.open && fileData?.serviceStatus?.isReopenedByProduct)
                        }
                        data-testid="CPLCreateNew"
                        plusIcon
                        size="small"
                      >
                        Add CPL
                      </AgentNetButton>
                    </div>
                  }
                  disableMargin="x"
                  menuItems={[
                    {
                      label: 'Knowledge Base',
                      link: KBLinks.cpl,
                    },
                  ]}
                />

                {cplActionsState.isEditing && (
                  <Notification
                    inline
                    severity="warning"
                    msg="You are in edit mode. Finish editing the CPL to enable all page features."
                    disablePadding
                    className={classes.warningMsg}
                  />
                )}
                {uploadCplDocument && (
                  <CplDocumentUploaderFC
                    isDrawerOpen={uploadCplDocument}
                    closeDrawer={() => setuploadCplDocument(false)}
                    onCancel={onCancel}
                  ></CplDocumentUploaderFC>
                )}
                {activeCplsCount >= 5 && (
                  <Notification inline severity="warning" className={classes.warningMsg}>
                    {openCplsLimit}
                  </Notification>
                )}
                {activeCplsCount === 0 && (
                  <Notification inline severity="warning" className={classes.warningMsg}>
                    {noCplsFound}
                  </Notification>
                )}
                {renderCplDetails(activeCpls)}
                {inactiveCpls.length ? (
                  <NewAgentNetDivider
                    title="Show Inactive CPLs"
                    {...(cplActionsState.isInactiveToggle && {
                      buttonName: 'Close All',
                      buttonName2: 'Expand All',
                    })}
                    onClick={() => {
                      dispatch({ type: CPLActions.expandCollapseInActive, payload: false });
                    }}
                    onClick2={() => {
                      dispatch({ type: CPLActions.expandCollapseInActive, payload: true });
                    }}
                    disablePaddingX
                    onSwitch={() => {
                      dispatch({ type: CPLActions.onInactiveToggleChange });
                    }}
                    switchValue={cplActionsState.isInactiveToggle}
                    buttonNameQaAttr="CPLCollapseAllInActive"
                    buttonName2QaAttr="CPLExpandAllInActive"
                    toggleQaAttr="CPLDisplayInvalid"
                  />
                ) : null}
                {cplActionsState.isInactiveToggle ? renderCplDetails(inactiveCpls) : null}

                <div>
                  <AgentNetConfirmationDialog
                    qaAttrPrefix="ConfirmationDelete"
                    disabled={deleteStatus === 'pending'}
                    onConfirm={handleDeleteCPLDialog}
                    open={openDeleteModal}
                    onDismissAction={handleCloseDialog}
                    dialogTitle="Delete CPL?"
                    dialogBtnContent="Yes, Continue"
                    dialogText="Are you sure you want to Delete this CPL?"
                  />
                  <AgentNetConfirmationDialog
                    qaAttrPrefix="ConfirmationVoid"
                    disabled={voidStatus === 'pending'}
                    onConfirm={handleVoidCPLDialog}
                    open={openVoidModal}
                    onDismissAction={handleCloseDialog}
                    dialogTitle="Void CPL?"
                    dialogBtnContent="Yes, Continue"
                    dialogText="Are you sure you want to void this CPL?"
                  />
                  <AgentNetConfirmationDialog
                    qaAttrPrefix="ConfirmationUnvoid"
                    disabled={unvoidStatus === 'pending'}
                    onConfirm={handleUnvoidCPLDialog}
                    open={openUnvoidModal}
                    onDismissAction={handleCloseDialog}
                    dialogTitle="Unvoid CPL?"
                    dialogBtnContent="Yes, Continue"
                    dialogText="Are you sure you want to unvoid this CPL?"
                  />
                </div>
              </Container>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CplList;
